import axios from '@fs/zion-axios'
import { i18n } from '@fs/zion-locale'
import { sgBaseUrl } from '@fs/zion-config'
import Cache from '@fs/zion-cache'

const INTELLIGENT_CHINESE_SURNAME_API_URL = `${sgBaseUrl}/service/discovery/chinese/surnames/search`
const SPECIFIC_CHINESE_SURNAME_API_URL = `${sgBaseUrl}/service/discovery/chinese/surnames`
export const SURNAMES_WITH_COMPLETE_DATA_AND_IMAGES = 489

const defaultCache = new Cache({ storeName: 'chineseSurnamesCache', type: 'session' })

const getHeaders = () => ({
  'Accept-Language': ['zh', 'en'].includes(i18n.language) ? i18n.language : 'en',
})

/**
 * Fetches a specific Chinese surname by ID
 * @param {string|number} surnameId - The ID of the surname to fetch
 * @param {boolean} useCache - Whether to use caching (default: true)
 * @returns {Promise<Object|null>} Surname object or null if not found
 */
export const fetchChineseSurnameById = async (surnameId, useCache = true) => {
  if (!surnameId) return null

  const cacheKey = `surname-${surnameId}-${i18n.language}`

  if (useCache && defaultCache) {
    const cachedData = await defaultCache.getItem(cacheKey)
    if (cachedData === '__404__') return null
    if (cachedData !== null) return cachedData
  }

  try {
    const { data } = await axios.get(`${SPECIFIC_CHINESE_SURNAME_API_URL}/${surnameId}`, {
      headers: getHeaders(),
    })

    if (useCache && defaultCache && data) {
      await defaultCache.setItem(cacheKey, data)
    }

    return data
  } catch (error) {
    console.error(`Error fetching Chinese surname with ID ${surnameId}:`, error)

    if (error.response?.status === 404) {
      if (useCache && defaultCache) {
        await defaultCache.setItem(cacheKey, '__404__', { ttl: 300 }) // Expires after 5 minutes
      }
      return null
    }

    throw error
  }
}

/**
 * Searches for Chinese surnames based on search text
 * @param {string} searchText - The text to search for
 * @param {number} limit - Maximum number of results to return (default: 5)
 * @param {boolean} useCache - Whether to use caching (default: true)
 * @returns {Promise<Object>} Object containing success status, results, and metadata
 */
export const searchChineseSurnames = async (searchText, limit = 5, useCache = true) => {
  if (!searchText?.trim()) {
    return { success: true, noResults: false, data: [] }
  }

  const cacheKey = `search-${encodeURIComponent(searchText.trim())}-${limit}-${i18n.language}`

  if (useCache && defaultCache) {
    const cachedData = await defaultCache.getItem(cacheKey)
    if (cachedData === '__404__') return { success: false, noResults: true, data: [], error: 'Not found' }
    if (cachedData !== null) return cachedData
  }

  try {
    const response = await axios.get(
      `${INTELLIGENT_CHINESE_SURNAME_API_URL}?text=${encodeURIComponent(searchText)}&limit=${limit}`,
      {
        headers: getHeaders(),
        validateStatus: (status) => (status >= 200 && status < 300) || status === 204,
      }
    )

    let result
    if (response.status === 204) {
      result = { success: true, noResults: true, data: [] }
    } else {
      const surnames = response.data
        .filter((surnameResponse) => surnameResponse.id <= SURNAMES_WITH_COMPLETE_DATA_AND_IMAGES)
        .map((surnameResponse) => ({
          id: surnameResponse.id,
          surname: surnameResponse.surname,
        }))

      result = { success: true, noResults: surnames.length === 0, data: surnames }
    }

    if (useCache && defaultCache) {
      await defaultCache.setItem(cacheKey, result)
    }

    return result
  } catch (error) {
    console.error('Error searching Chinese surnames:', error)

    if (error.response?.status === 404 && useCache && defaultCache) {
      await defaultCache.setItem(cacheKey, '__404__', { ttl: 300 }) // Expires after 5 minutes
    }

    return { success: false, noResults: true, data: [], error }
  }
}
